import { useCallback, useEffect, useSyncExternalStore } from 'react';
import { PLAYER_API_URL } from 'utils/urls';

import { parsePayoutRows, PayoutRow } from 'components/TournamentPayouts/payouts';

type Snapshot = { rows: PayoutRow[]; status: 'loading' | 'ready' | 'unavailable' };
const loading: Snapshot = { rows: [], status: 'loading' };
const unavailable: Snapshot = { rows: [], status: 'unavailable' };
const stores = new Map<number, ReturnType<typeof createStore>>();
let active = 0;
const queue: Array<() => void> = [];
function schedule(run: () => Promise<void>) {
  const start = () => {
    active++;
    void run().finally(() => {
      active--;
      queue.shift()?.();
    });
  };
  if (active < 4) start();
  else queue.push(start);
}
function createStore(id: number) {
  let snapshot = loading;
  let updated = 0;
  let pending = false;
  const listeners = new Set<() => void>();
  const publish = (next: Snapshot) => {
    snapshot = next;
    listeners.forEach((fn) => fn());
  };
  const request = (force = false) => {
    if (pending || !listeners.size || (!force && Date.now() - updated < 30000)) return;
    pending = true;
    schedule(async () => {
      if (!listeners.size) {
        pending = false;
        return;
      }
      const controller = new AbortController();
      const timeout = setTimeout(() => controller.abort(), 10000);
      try {
        const response = await fetch(`${PLAYER_API_URL}/sng/${id}/payouts`, {
          signal: controller.signal,
          credentials: 'omit'
        });
        if (!response.ok) throw new Error('Payout request failed');
        const body = await response.json();
        if (Number(body.tournamentId) !== id || !Array.isArray(body.response))
          throw new Error('Invalid payouts');
        const rows = parsePayoutRows(body.response);
        if (!rows?.length) throw new Error('Missing payouts');
        updated = Date.now();
        publish({ rows, status: 'ready' });
      } catch {
        publish(unavailable);
      } finally {
        clearTimeout(timeout);
        pending = false;
      }
    });
  };
  return {
    snapshot: () => snapshot,
    request,
    unused: () => !listeners.size && !pending,
    subscribe: (fn: () => void) => {
      if (!listeners.size && Date.now() - updated >= 30000) snapshot = loading;
      listeners.add(fn);
      return () => {
        listeners.delete(fn);
      };
    }
  };
}
const noSubscribe = () => () => {};
const noSnapshot = () => loading;

/** Public, display-only SnG payouts shared by both modals and lobby preloads. */
export function useSngPayouts(
  id: number,
  enabled: boolean,
  refreshKey?: unknown,
  keepFresh = true
) {
  let store: ReturnType<typeof createStore> | undefined;
  if (enabled && Number.isSafeInteger(id) && id > 0) {
    store = stores.get(id);
    if (!store) {
      if (stores.size >= 100) {
        for (const [key, candidate] of Array.from(stores)) {
          if (candidate.unused()) stores.delete(key);
          if (stores.size < 100) break;
        }
      }
      store = createStore(id);
      stores.set(id, store);
    }
  }
  const snapshot = useSyncExternalStore(
    store?.subscribe ?? noSubscribe,
    store?.snapshot ?? noSnapshot
  );
  const retry = useCallback(() => store?.request(true), [store]);
  useEffect(() => {
    store?.request();
  }, [store, refreshKey]);
  useEffect(() => {
    if (!store || !keepFresh) return;
    const current = store;
    const timer = setInterval(() => current.request(), 30000);
    const refresh = () => current.request();
    window.addEventListener('online', refresh);
    window.addEventListener('focus', refresh);
    return () => {
      clearInterval(timer);
      window.removeEventListener('online', refresh);
      window.removeEventListener('focus', refresh);
    };
  }, [store, keepFresh]);
  return { ...snapshot, retry };
}
